// World games: the rules, as pure functions (unit-tested in tests/harvest*.test.ts).
//
//   - Hunter paths: where a patrol is at a given shared time. Deterministic,
//     so every client computes the same positions from the same clock with no
//     network traffic.
//   - A run: start, collect, get caught, finish; elapsed time with penalties.
//   - Scoring: fastest time, stored on the arcade boards as points
//     (POINTS_BASE − milliseconds), so higher points = faster.
//
// No React / three.js here.

import { WORLD_GAME_DEFAULTS, type WorldGameConfig } from './worldGameTypes';

export type Vec2 = [number, number];

// ---- hunter paths ---------------------------------------------------------------

export interface PatrolLeg {
  from: Vec2;
  to: Vec2;
  /** Seconds walking this leg. */
  walk: number;
  /** Seconds paused at `to` after arriving. */
  pause: number;
}

export interface PatrolPlan {
  legs: PatrolLeg[];
  /** Seconds for one full round. */
  cycle: number;
  speed: number;
}

/** The legs of one full round: a loop returns to the first point; otherwise it walks there and back. */
export function patrolPlan(cfg: Pick<WorldGameConfig, 'path' | 'loop' | 'speed' | 'pause'>): PatrolPlan | null {
  const pts = (cfg.path ?? []).filter((p) => Number.isFinite(p?.[0]) && Number.isFinite(p?.[1]));
  if (pts.length < 2) return null;
  const speed = Math.max(0.1, cfg.speed ?? WORLD_GAME_DEFAULTS.speed);
  const pause = Math.max(0, cfg.pause ?? WORLD_GAME_DEFAULTS.pause);
  const seq: Vec2[] = cfg.loop ? [...pts, pts[0]] : [...pts, ...pts.slice(0, -1).reverse()];
  const legs: PatrolLeg[] = [];
  for (let i = 0; i < seq.length - 1; i++) {
    const a = seq[i], b = seq[i + 1];
    const d = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (d < 1e-6) continue;
    legs.push({ from: a, to: b, walk: d / speed, pause });
  }
  if (!legs.length) return null;
  const cycle = legs.reduce((s, l) => s + l.walk + l.pause, 0);
  return { legs, cycle, speed };
}

export interface PatrolPose {
  x: number;
  z: number;
  /** Facing (yaw: 0 = +Z, the game's portal convention), radians. */
  yaw: number;
  /** false while paused at a waypoint (looking around). */
  moving: boolean;
  /** 0–1 through the current pause (for the look-around turn). */
  pauseT: number;
}

const mod = (a: number, n: number) => ((a % n) + n) % n;

/** Where a hunter is at shared time `tSec` (seconds since the Unix epoch on the shared clock). */
export function patrolAt(plan: PatrolPlan, tSec: number, phase = 0): PatrolPose {
  let t = mod(tSec + phase * plan.cycle, plan.cycle);
  for (const l of plan.legs) {
    const yaw = Math.atan2(l.to[0] - l.from[0], l.to[1] - l.from[1]);
    if (t < l.walk) {
      const k = t / l.walk;
      return { x: l.from[0] + (l.to[0] - l.from[0]) * k, z: l.from[1] + (l.to[1] - l.from[1]) * k, yaw, moving: true, pauseT: 0 };
    }
    t -= l.walk;
    if (t < l.pause) return { x: l.to[0], z: l.to[1], yaw, moving: false, pauseT: l.pause > 0 ? t / l.pause : 0 };
    t -= l.pause;
  }
  const last = plan.legs[plan.legs.length - 1];
  return { x: last.to[0], z: last.to[1], yaw: Math.atan2(last.to[0] - last.from[0], last.to[1] - last.from[1]), moving: false, pauseT: 1 };
}

/** Total walking length of one round (metres). */
export const patrolLength = (plan: PatrolPlan) => plan.legs.reduce((s, l) => s + l.walk * plan.speed, 0);

/**
 * Where to put a caught player back: the first candidate (newest checkpoint
 * first, the start last) that no hunter comes within `clear` metres of over
 * the next `ahead` seconds, so nobody respawns into a hunter's path and gets
 * caught again. With no safe one, the candidate hunters stay furthest from.
 */
export function safestRespawn(candidates: Vec2[], hunters: { plan: PatrolPlan; phase: number }[], tSec: number, clear = 5, ahead = 5): number {
  let best = 0, bestD = -1;
  for (let i = 0; i < candidates.length; i++) {
    const [cx, cz] = candidates[i];
    let d = Infinity;
    for (const h of hunters) for (let dt = 0; dt <= ahead; dt += 0.5) {
      const p = patrolAt(h.plan, tSec + dt, h.phase);
      d = Math.min(d, Math.hypot(p.x - cx, p.z - cz));
    }
    if (d >= clear) return i;
    if (d > bestD) { bestD = d; best = i; }
  }
  return best;
}

// ---- geometry ---------------------------------------------------------------------

export const dist2D = (ax: number, az: number, bx: number, bz: number) => Math.hypot(ax - bx, az - bz);

/** Is world point (x, y, z) inside a box primitive (centre, yaw, full size)? */
export function inBox(p: [number, number, number], center: [number, number, number], yaw: number, size: [number, number, number]): boolean {
  const dx = p[0] - center[0], dz = p[2] - center[2];
  const c = Math.cos(-yaw), s = Math.sin(-yaw);
  const lx = dx * c + dz * s, lz = -dx * s + dz * c;
  return Math.abs(lx) <= size[0] / 2 && Math.abs(lz) <= size[2] / 2 && Math.abs(p[1] - center[1]) <= size[1] / 2 + 1.0;
}

// ---- a run ------------------------------------------------------------------------

export type RunPhase = 'idle' | 'countdown' | 'playing' | 'won' | 'lost';

export interface RunState {
  phase: RunPhase;
  game: string;
  /** Local performance.now() ms when play began (after the countdown). */
  startedAt: number;
  /** When the countdown ends (local ms). */
  goAt: number;
  /** Collected item ids, in pick-up order. */
  collected: string[];
  /** Where each collected item was (respawn checkpoints), parallel to `collected`. */
  spots: Vec2[];
  catches: number;
  penaltyMs: number;
  /** Time taken off by bonuses (golden pumpkin). */
  bonusMs: number;
  /** Bonus / power-up pieces used this run. */
  extras: string[];
  /** No catches before this (local ms): the moments after a respawn. */
  graceUntil: number;
  finishedAt: number;
  /** Final time (ms, penalties included) once won. */
  timeMs: number;
  lostReason: 'time' | 'lives' | null;
  race: boolean;
}

export interface RunRules {
  required: number;
  timeLimitMs: number;
  lives: number;
  penalty: 'item' | 'time';
  penaltyMs: number;
}

export const GRACE_MS = 2500;
export const COUNTDOWN_MS = 3000;

export function rulesFrom(start: WorldGameConfig | null | undefined, itemCount: number): RunRules {
  const d = WORLD_GAME_DEFAULTS;
  const req = Math.round(start?.required ?? d.required);
  return {
    required: req > 0 ? Math.min(req, itemCount) : itemCount,
    timeLimitMs: Math.max(0, (start?.timeLimit ?? d.timeLimit) * 1000),
    lives: Math.max(0, Math.round(start?.lives ?? d.lives)),
    penalty: start?.penalty ?? d.penalty,
    penaltyMs: Math.max(0, (start?.penaltySeconds ?? d.penaltySeconds) * 1000),
  };
}

export const idleRun = (game = ''): RunState => ({
  phase: 'idle', game, startedAt: 0, goAt: 0, collected: [], spots: [], catches: 0, penaltyMs: 0, bonusMs: 0, extras: [],
  graceUntil: 0, finishedAt: 0, timeMs: 0, lostReason: null, race: false,
});

/** Begin a run: a countdown, then play. `goAt` lets a race start everyone at the same moment. */
export function startRun(game: string, now: number, goAt = now + COUNTDOWN_MS, race = false): RunState {
  return { ...idleRun(game), phase: goAt > now ? 'countdown' : 'playing', goAt, startedAt: goAt, graceUntil: goAt + GRACE_MS, race };
}

/** Advance the countdown; returns true when play just began. */
export function tickRun(run: RunState, now: number, rules: RunRules): { started: boolean; timedOut: boolean } {
  let started = false, timedOut = false;
  if (run.phase === 'countdown' && now >= run.goAt) { run.phase = 'playing'; run.startedAt = run.goAt; started = true; }
  if (run.phase === 'playing' && rules.timeLimitMs > 0 && elapsedMs(run, now) >= rules.timeLimitMs) {
    run.phase = 'lost'; run.lostReason = 'time'; run.finishedAt = now; timedOut = true;
  }
  return { started, timedOut };
}

/** Time on the clock (ms), penalties included. */
export function elapsedMs(run: RunState, now: number): number {
  if (run.phase === 'idle' || run.phase === 'countdown') return 0;
  const end = run.phase === 'won' || run.phase === 'lost' ? run.finishedAt : now;
  return Math.max(0, Math.max(0, end - run.startedAt) + run.penaltyMs - run.bonusMs);
}

/** Pick up a bonus or power-up piece (once per run). `bonusMs` comes off the time. */
export function takeExtra(run: RunState, id: string, bonusMs = 0): boolean {
  if (run.phase !== 'playing' || run.extras.includes(id)) return false;
  run.extras.push(id);
  run.bonusMs += Math.max(0, bonusMs);
  return true;
}

/** Pick up item `id` at `spot`. False if not playing or already held. */
export function collect(run: RunState, id: string, spot: Vec2): boolean {
  if (run.phase !== 'playing' || run.collected.includes(id)) return false;
  run.collected.push(id);
  run.spots.push(spot);
  return true;
}

export interface CatchResult {
  /** The item that went back to its spot (penalty 'item'). */
  dropped: string | null;
  /** Milliseconds added. */
  addedMs: number;
  /** Where to put the player back: the last checkpoint kept, or null = the start. */
  respawn: Vec2 | null;
  /** The run is over (out of lives). */
  out: boolean;
}

/** A hunter caught the player. No-op (null) during the grace period or when not playing. */
export function caught(run: RunState, now: number, rules: RunRules): CatchResult | null {
  if (run.phase !== 'playing' || now < run.graceUntil) return null;
  run.catches++;
  let dropped: string | null = null;
  let addedMs = 0;
  if (rules.penalty === 'item' && run.collected.length) {
    dropped = run.collected.pop()!;
    run.spots.pop();
  } else {
    addedMs = rules.penaltyMs;
  }
  run.penaltyMs += addedMs;
  run.graceUntil = now + GRACE_MS;
  const out = rules.lives > 0 && run.catches >= rules.lives;
  if (out) { run.phase = 'lost'; run.lostReason = 'lives'; run.finishedAt = now; }
  const respawn = run.spots.length ? run.spots[run.spots.length - 1] : null;
  return { dropped, addedMs, respawn, out };
}

/** At the goal: finish if holding enough. Returns how many are still missing (0 = finished now). */
export function tryFinish(run: RunState, now: number, rules: RunRules): number {
  if (run.phase !== 'playing') return -1;
  const missing = Math.max(0, rules.required - run.collected.length);
  if (missing > 0) return missing;
  run.phase = 'won';
  run.finishedAt = now;
  run.timeMs = elapsedMs(run, now);
  return 0;
}

// ---- scoring ----------------------------------------------------------------------

/**
 * Times go on the shared arcade boards (higher = better) as points:
 * POINTS_BASE − milliseconds. One hour is the slowest time that scores.
 */
export const POINTS_BASE = 3_600_000;

export function timeToPoints(ms: number): number {
  const t = Math.round(ms);
  if (!(t > 0) || t >= POINTS_BASE) return 0;
  return POINTS_BASE - t;
}

export function pointsToTime(points: number): number {
  return Math.max(0, POINTS_BASE - Math.round(points));
}

/** 2:31.4 (or 0:07.9); hours never happen (scores stop at an hour). */
export function formatTime(ms: number, tenths = true): string {
  const t = Math.max(0, Math.floor(ms));
  const m = Math.floor(t / 60000);
  const s = Math.floor((t % 60000) / 1000);
  const d = Math.floor((t % 1000) / 100);
  return `${m}:${String(s).padStart(2, '0')}${tenths ? `.${d}` : ''}`;
}
